import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, ClipboardList, Calendar, Building2, DollarSign, TrendingUp, Users, PiggyBank, GitBranch, Shield, FileText, BookOpen } from "lucide-react";
import { ConversationalAssessment } from "@/components/assessment/ConversationalAssessment";
import { AssessmentModule } from "@/types/assessment";
import { useAssessment } from "@/context/AssessmentContext";
import { getAssessmentMetaByModuleId } from "@/utils/assessmentLookup";

const moduleIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'owner-readiness': ClipboardList,
  'owner-readiness-exit-timeline': Calendar,
  'business-profile': Building2,
  'business-valuation': DollarSign,
  'business-optimization': TrendingUp,
  'advisory-team': Users,
  'financial-readiness': PiggyBank,
  'successor-strategy': GitBranch,
  'governance-family': Shield,
  'exit-structure': FileText,
  'mysuccession-plan': BookOpen,
};

interface AssessmentOverlayProps {
  module: AssessmentModule | null;
  onOpenChange: (open: boolean) => void;
  /** Called after completion — close overlay and open next assessment. Receives the current module_id for exclusion. */
  onNextAssessment?: (completedModuleId: string) => void;
  /** Called after completion — used for query invalidation */
  onCompleted?: () => void;
}

export function AssessmentOverlay({ module, onOpenChange, onNextAssessment, onCompleted }: AssessmentOverlayProps) {
  const { updateModuleStatus } = useAssessment();
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [sequenceOrder, setSequenceOrder] = useState<number | null>(null);

  useEffect(() => {
    if (module?.id) {
      getAssessmentMetaByModuleId(module.id).then(meta => {
        setSequenceOrder(meta?.sequenceOrder ?? null);
      });
    }
  }, [module?.id]);

  if (!module) return null;

  const IconComponent = moduleIconMap[module.id] || ClipboardList;

  const handleClose = () => {
    setShowExitConfirm(false);
    onOpenChange(false);
  };

  const handleComplete = () => {
    if (module) {
      updateModuleStatus(module.id, "complete");
    }
    onCompleted?.();
    handleClose();
  };

  const handleNextAssessment = () => {
    if (module) {
      updateModuleStatus(module.id, "complete");
    }
    onCompleted?.();
    handleClose();
    // Defer to allow overlay to close before opening next
    setTimeout(() => {
      onNextAssessment?.(module.id);
    }, 100);
  };

  const handleXClick = () => {
    setShowExitConfirm(true);
  };

  return (
    <Dialog open={!!module} onOpenChange={(open) => {
      if (!open) {
        setShowExitConfirm(true);
      }
    }}>
      <DialogContent 
        className="w-full max-w-4xl h-[80vh] flex flex-col p-0 rounded-xl overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        // Hide the default close button from DialogContent
        data-hide-close
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{module.title}</DialogTitle>
          <DialogDescription>
            {module.description}
          </DialogDescription>
        </DialogHeader>

        {/* Header bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-background flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <IconComponent className="h-5 w-5 text-primary" />
            <span className="font-medium text-[15px] text-foreground">{module.title}</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleXClick}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close assessment"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="h-full flex flex-col overflow-hidden relative">
          {showExitConfirm ? (
            <div className="flex-1 flex items-center justify-center px-6">
              <div className="max-w-sm text-center space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Your progress is saved</h3>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to exit? You can return and continue at any time.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className="text-destructive border-destructive/30 hover:bg-destructive/10"
                  >
                    Exit Assessment
                  </Button>
                  <Button
                    onClick={() => setShowExitConfirm(false)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Continue Assessment
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <ConversationalAssessment 
              moduleId={module.id}
              onComplete={handleComplete}
              onExit={handleClose}
              onNextAssessment={handleNextAssessment}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
